'use client';

// ============================================================================
// WorkItemSuppliersPanel — multi-supplier table for a product detail screen.
// Date: April 21, 2026
// ============================================================================

import React, { useMemo, useState } from 'react';
import { Star, Trash2, Plus, X, Pencil } from 'lucide-react';
import {
  useWorkItemSuppliers,
  type SupplierCreatePayload,
  type WorkItemSupplier,
} from '@/hooks/useWorkItemSuppliers';
import { confirm } from '@/lib/notifications';

interface Props {
  workItemId: string;
  productName?: string;
  // Caller supplies the list of supplier contacts (id + name).
  supplierContacts: Array<{ id: number; name: string }>;
}

function formatCurrency(amount: number | null | undefined, currency = 'ZAR') {
  if (amount == null || !Number.isFinite(Number(amount))) {
    return '—';
  }

  try {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount));
  } catch {
    return `${currency} ${Number(amount).toFixed(2)}`;
  }
}

function getSupplierDisplayName(
  supplier: Pick<WorkItemSupplier, 'supplier_contact_id' | 'supplier_name'>,
  supplierContacts: Array<{ id: number; name: string }>,
) {
  return supplier.supplier_name
    || supplierContacts.find((contact) => contact.id === supplier.supplier_contact_id)?.name
    || `Supplier #${supplier.supplier_contact_id}`;
}

export default function WorkItemSuppliersPanel({ workItemId, productName, supplierContacts }: Props) {
  const { suppliers, loading, error, create, update, remove, setPreferred } = useWorkItemSuppliers(workItemId);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState<WorkItemSupplier | null>(null);

  const preferredSupplier = useMemo(
    () => suppliers.find((supplier) => supplier.is_preferred) || null,
    [suppliers],
  );
  const backupCount = Math.max(suppliers.length - (preferredSupplier ? 1 : 0), 0);

  return (
    <div className="bg-white border rounded-lg p-4 space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="font-semibold">Suppliers{productName ? ` — ${productName}` : ''}</h3>
          <p className="mt-1 text-xs text-gray-500">
            Maintain the preferred source, backup suppliers, and per-supplier buying terms for this product.
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-1 font-medium text-blue-700">
              {suppliers.length} mapped supplier{suppliers.length === 1 ? '' : 's'}
            </span>
            <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 font-medium ${preferredSupplier ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
              {preferredSupplier
                ? `Preferred: ${getSupplierDisplayName(preferredSupplier, supplierContacts)}`
                : 'No preferred supplier selected'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
              {backupCount} backup supplier{backupCount === 1 ? '' : 's'}
            </span>
            {preferredSupplier?.priority_rank != null && (
              <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 px-2.5 py-1 font-medium text-purple-700">
                Rank #{preferredSupplier.priority_rank}
              </span>
            )}
          </div>
        </div>

        <button
          onClick={() => setShowCreateForm(true)}
          className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded inline-flex items-center gap-1 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" /> Add Supplier
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {loading ? (
        <div className="text-sm text-gray-500">Loading…</div>
      ) : suppliers.length === 0 ? (
        <div className="rounded-lg border border-dashed px-4 py-6 text-sm text-gray-500">
          No suppliers mapped yet. Add your preferred supplier first, then capture backup sources for stock-outs or pricing changes.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-left p-2">Supplier</th>
                <th className="text-left p-2">SKU</th>
                <th className="text-right p-2">Cost</th>
                <th className="text-right p-2">Lead (d)</th>
                <th className="text-right p-2">MOQ</th>
                <th className="text-center p-2">Default</th>
                <th className="text-right p-2">Last Buy</th>
                <th className="text-right p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((supplier) => (
                <tr key={supplier.id} className="border-t align-top">
                  <td className="p-2">
                    <div className="min-w-[220px]">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-gray-900">
                          {getSupplierDisplayName(supplier, supplierContacts)}
                        </span>
                        {supplier.is_preferred && (
                          <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                            Preferred
                          </span>
                        )}
                        {supplier.priority_rank != null && (
                          <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-[10px] font-semibold text-purple-700">
                            Rank #{supplier.priority_rank}
                          </span>
                        )}
                        {supplier.pack_size != null && (
                          <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-semibold text-gray-600">
                            Pack {supplier.pack_size}
                          </span>
                        )}
                      </div>
                      {(supplier.selection_reason || supplier.fallback_reason) && (
                        <div className="mt-1 space-y-0.5 text-xs text-gray-500">
                          {supplier.selection_reason && (
                            <p><span className="font-semibold text-gray-600">Preferred because:</span> {supplier.selection_reason}</p>
                          )}
                          {supplier.fallback_reason && (
                            <p><span className="font-semibold text-gray-600">Use as backup when:</span> {supplier.fallback_reason}</p>
                          )}
                        </div>
                      )}
                      {supplier.notes && (
                        <p className="mt-1 max-w-md text-xs text-gray-500 whitespace-pre-wrap">
                          {supplier.notes}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="p-2 font-mono text-xs">{supplier.supplier_sku || '—'}</td>
                  <td className="p-2 text-right">
                    {formatCurrency(supplier.cost_price, supplier.currency_code || 'ZAR')}
                  </td>
                  <td className="p-2 text-right">{supplier.lead_time_days ?? '—'}</td>
                  <td className="p-2 text-right">{supplier.min_order_qty ?? '—'}</td>
                  <td className="p-2 text-center">
                    <button
                      onClick={() => !supplier.is_preferred && setPreferred(supplier.id)}
                      title={supplier.is_preferred ? 'Default supplier' : 'Make default'}
                    >
                      <Star className={`w-4 h-4 inline ${supplier.is_preferred ? 'fill-yellow-400 text-yellow-500' : 'text-gray-300'}`} />
                    </button>
                  </td>
                  <td className="p-2 text-right text-xs text-gray-500">
                    {supplier.last_purchase_date
                      ? `${new Date(supplier.last_purchase_date).toLocaleDateString()}${supplier.last_purchase_cost != null ? ` @ ${formatCurrency(supplier.last_purchase_cost, supplier.currency_code || 'ZAR')}` : ''}`
                      : '—'}
                  </td>
                  <td className="p-2 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditingSupplier(supplier)}
                        className="text-blue-600 hover:text-blue-800"
                        title="Edit supplier terms"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={async () => { if ((await confirm({ message: 'Remove this supplier mapping?', variant: 'danger' }))) remove(supplier.id); }}
                        className="text-red-600 hover:text-red-800"
                        title="Remove"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateForm && (
        <SupplierForm
          mode="create"
          workItemId={workItemId}
          supplierContacts={supplierContacts}
          existingSupplierIds={suppliers.map((supplier) => supplier.supplier_contact_id)}
          onClose={() => setShowCreateForm(false)}
          onCreate={async (payload) => {
            await create(payload);
            setShowCreateForm(false);
          }}
          onUpdate={async () => {}}
        />
      )}

      {editingSupplier && (
        <SupplierForm
          mode="edit"
          workItemId={workItemId}
          supplier={editingSupplier}
          supplierContacts={supplierContacts}
          existingSupplierIds={suppliers.map((supplier) => supplier.supplier_contact_id)}
          onClose={() => setEditingSupplier(null)}
          onCreate={async () => {}}
          onUpdate={async (id, payload) => {
            await update(id, payload);
            setEditingSupplier(null);
          }}
        />
      )}
    </div>
  );
}

interface FormProps {
  mode: 'create' | 'edit';
  workItemId: string;
  supplier?: WorkItemSupplier | null;
  supplierContacts: Array<{ id: number; name: string }>;
  existingSupplierIds: number[];
  onClose: () => void;
  onCreate: (payload: SupplierCreatePayload) => Promise<void>;
  onUpdate: (id: number, payload: Partial<WorkItemSupplier>) => Promise<void>;
}

function SupplierForm({
  mode,
  workItemId,
  supplier,
  supplierContacts,
  existingSupplierIds,
  onClose,
  onCreate,
  onUpdate,
}: FormProps) {
  const available = useMemo(() => {
    if (mode === 'edit') {
      return supplierContacts;
    }

    return supplierContacts.filter((contact) => !existingSupplierIds.includes(contact.id));
  }, [existingSupplierIds, mode, supplierContacts]);

  const [supplierId, setSupplierId] = useState<number | ''>(supplier?.supplier_contact_id ?? '');
  const [sku, setSku] = useState(supplier?.supplier_sku || '');
  const [cost, setCost] = useState(supplier?.cost_price != null ? String(supplier.cost_price) : '');
  const [currency, setCurrency] = useState(supplier?.currency_code || 'ZAR');
  const [lead, setLead] = useState(supplier?.lead_time_days != null ? String(supplier.lead_time_days) : '');
  const [moq, setMoq] = useState(supplier?.min_order_qty != null ? String(supplier.min_order_qty) : '');
  const [packSize, setPackSize] = useState(supplier?.pack_size != null ? String(supplier.pack_size) : '');
  const [priorityRank, setPriorityRank] = useState(supplier?.priority_rank != null ? String(supplier.priority_rank) : '');
  const [selectionReason, setSelectionReason] = useState(supplier?.selection_reason || '');
  const [fallbackReason, setFallbackReason] = useState(supplier?.fallback_reason || '');
  const [notes, setNotes] = useState(supplier?.notes || '');
  const [pref, setPref] = useState(Boolean(supplier?.is_preferred));
  const [submitting, setSubmitting] = useState(false);

  const selectedSupplierName = supplier
    ? getSupplierDisplayName(supplier, supplierContacts)
    : available.find((contact) => contact.id === supplierId)?.name || '';

  return (
    <div className="fixed inset-0 z-[180] flex items-center justify-center bg-black/40" style={{ pointerEvents: 'auto' }}>
      <div className="bg-white rounded-lg p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-semibold">{mode === 'edit' ? 'Edit Supplier Terms' : 'Add Supplier'}</h3>
            <p className="text-xs text-gray-500 mt-1">
              {mode === 'edit'
                ? 'Update the buying terms, service notes, and preference for this supplier relationship.'
                : 'Capture supplier-specific pricing, lead time, pack size, and why this source matters.'}
            </p>
          </div>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>

        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Supplier</label>
            {mode === 'edit' ? (
              <div className="w-full rounded border bg-gray-50 px-3 py-2 text-gray-700">
                {selectedSupplierName}
              </div>
            ) : (
              <select
                value={supplierId}
                onChange={(e) => setSupplierId(e.target.value ? Number(e.target.value) : '')}
                className="w-full border rounded px-2 py-1.5"
              >
                <option value="">Choose…</option>
                {available.map((contact) => (
                  <option key={contact.id} value={contact.id}>{contact.name}</option>
                ))}
              </select>
            )}
            {mode === 'create' && available.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                All supplier/vendor contacts are already linked to this product.
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Supplier SKU</label>
              <input value={sku} onChange={(e) => setSku(e.target.value)} className="w-full border rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Currency</label>
              <input value={currency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} maxLength={3} className="w-full border rounded px-2 py-1.5" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Cost</label>
              <input type="number" step="0.01" value={cost} onChange={(e) => setCost(e.target.value)} className="w-full border rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">Pack Size</label>
              <input type="number" step="0.01" value={packSize} onChange={(e) => setPackSize(e.target.value)} className="w-full border rounded px-2 py-1.5" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="block text-xs text-gray-600 mb-1">Lead (days)</label>
              <input type="number" value={lead} onChange={(e) => setLead(e.target.value)} className="w-full border rounded px-2 py-1.5" />
            </div>
            <div>
              <label className="block text-xs text-gray-600 mb-1">MOQ</label>
              <input type="number" step="0.01" value={moq} onChange={(e) => setMoq(e.target.value)} className="w-full border rounded px-2 py-1.5" />
            </div>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Priority rank</label>
            <input
              type="number"
              min="1"
              step="1"
              value={priorityRank}
              onChange={(e) => setPriorityRank(e.target.value)}
              placeholder="1 = strongest source after preferred flag"
              className="w-full border rounded px-2 py-1.5"
            />
            <p className="mt-1 text-[11px] text-gray-500">
              Use this to order backup suppliers when multiple sources are mapped.
            </p>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Preferred selection reason</label>
            <textarea
              value={selectionReason}
              onChange={(e) => setSelectionReason(e.target.value)}
              rows={2}
              placeholder="Best price, service levels, local stock, quality, SLA, etc."
              className="w-full border rounded px-2 py-1.5"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Backup / fallback reason</label>
            <textarea
              value={fallbackReason}
              onChange={(e) => setFallbackReason(e.target.value)}
              rows={2}
              placeholder="Use when preferred supplier has no stock, long lead time, or pricing spikes."
              className="w-full border rounded px-2 py-1.5"
            />
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Notes / rationale</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Why is this supplier preferred, or when should they be used as a backup?"
              className="w-full border rounded px-2 py-1.5"
            />
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={pref} onChange={(e) => setPref(e.target.checked)} />
            Mark as preferred / default supplier
          </label>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1.5 border rounded text-sm">Cancel</button>
          <button
            disabled={(mode === 'create' && !supplierId) || submitting || (mode === 'create' && available.length === 0)}
            onClick={async () => {
              if (mode === 'create' && !supplierId) return;
              if (mode === 'edit' && !supplier) return;

              setSubmitting(true);
              try {
                if (mode === 'create') {
                  await onCreate({
                    work_item_id: workItemId,
                    supplier_contact_id: Number(supplierId),
                    supplier_sku: sku || null,
                    cost_price: cost ? Number(cost) : null,
                    currency_code: currency,
                    lead_time_days: lead ? Number(lead) : null,
                    min_order_qty: moq ? Number(moq) : null,
                    pack_size: packSize ? Number(packSize) : null,
                    is_preferred: pref,
                    priority_rank: priorityRank ? Number(priorityRank) : null,
                    selection_reason: selectionReason.trim() || null,
                    fallback_reason: fallbackReason.trim() || null,
                    notes: notes.trim() || null,
                  });
                } else {
                  const supplierToUpdate = supplier;
                  if (!supplierToUpdate) {
                    return;
                  }

                  await onUpdate(supplierToUpdate.id, {
                    supplier_sku: sku || null,
                    cost_price: cost ? Number(cost) : null,
                    currency_code: currency,
                    lead_time_days: lead ? Number(lead) : null,
                    min_order_qty: moq ? Number(moq) : null,
                    pack_size: packSize ? Number(packSize) : null,
                    is_preferred: pref,
                    priority_rank: priorityRank ? Number(priorityRank) : null,
                    selection_reason: selectionReason.trim() || null,
                    fallback_reason: fallbackReason.trim() || null,
                    notes: notes.trim() || null,
                  });
                }
              } finally {
                setSubmitting(false);
              }
            }}
            className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded disabled:opacity-50"
          >
            {submitting ? 'Saving…' : mode === 'edit' ? 'Save Changes' : 'Add Supplier'}
          </button>
        </div>
      </div>
    </div>
  );
}
